import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Collapse,
  Box,
} from '@mui/material'
import { withStyles } from '@mui/styles'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

import { basicTableStyles } from '../basic-table/basic-table.styles'

const defaultKey = (i, row) => i

/**
 * @typedef {Object} ColumnConfig
 * @property {string} label - El texto que se mostrará en la cabecera de la columna.
 * @property {string} key - La clave para acceder al valor correspondiente en el objeto de datos de la fila.
 * @property {'left' | 'right' | 'center'} [align='left'] - La alineación del contenido de la celda.
 * @property {string} [type] - El tipo de dato para aplicar un formato especial. Valores posibles: 'date', 'datetime', 'component', 'responsable', 'comboDocumentData', 'comboData'.
 * @property {React.ComponentType<any>} [component] - El componente de React a renderizar si el `type` es 'component'. Recibirá `value`, `context` y `row` como props.
 */

/**
 * Un componente de tabla de Material-UI que renderiza filas que pueden expandirse
 * para mostrar contenido adicional. Es altamente configurable a través de props
 * para definir columnas, formatear datos y renderizar el contenido detallado.
 *
 * @component
 * @example
 * const data = [
 * { id: 1, name: 'Producto A', date: '2025-10-20T10:00:00Z', fields: [{ detail: 'Info extra 1' }] },
 * { id: 2, name: 'Producto B', date: '2025-10-21T11:30:00Z', fields: [] },
 * ];
 *
 * const config = [
 * { label: 'Nombre', key: 'name' },
 * { label: 'Fecha de Creación', key: 'date', type: 'datetime', align: 'right' },
 * ];
 *
 * const renderDetails = (row) => <div>{row.fields[0].detail}</div>;
 *
 * <CollapsibleTable
 * data={data}
 * config={config}
 * renderDetails={renderDetails}
 * rowKey={(i, row) => row.id}
 * />
 *
 * @param {Object} props - Las props del componente CollapsibleTable.
 * @param {Function} [props.rowKey=defaultKey] - Función para generar una clave única para cada fila. Recibe el índice y el objeto de la fila. Por defecto, usa el índice.
 * @param {Object} props.classes - Objeto con los nombres de las clases CSS inyectado por `withStyles` para la estilización interna.
 * @param {ColumnConfig[]} props.config - Array de objetos de configuración para definir las columnas de la tabla.
 * @param {Object[]} props.data - Array de objetos que representan los datos a mostrar en las filas de la tabla.
 * @param {any} [props.context] - Un objeto de contexto opcional que se pasa a los componentes de celda personalizados.
 * @param {Function} [props.getdata] - Prop de función recibida. (Nota: esta prop no se utiliza actualmente en la lógica del componente).
 * @param {'small' | 'medium'} [props.size='medium'] - El tamaño de la tabla, que afecta al padding de las celdas.
 * @param {boolean} [props.showHeaders=true] - Si es `true`, se renderizará la cabecera de la tabla.
 * @param {Function} props.renderDetails - Una función `render prop` que devuelve el componente a mostrar en la sección colapsable de una fila. Recibe el objeto completo de la fila (`row`) como argumento.
 * @param {Object} [props.onClick] - Una función que se ejecuta cuando se hace clic en una fila. Si se proporciona, la fila será interactiva.
 * @param {boolean} [props.loading] - Si es `true` y `onClick` está presente, la fila mostrará un estilo de carga.
 */
export const CollapsibleTable = withStyles(basicTableStyles)(({
  rowKey = defaultKey,
  classes,
  config,
  data,
  context,
  getdata,
  size = 'medium',
  showHeaders = true,
  renderDetails,
  ...props
}) => {
  const { formatDate, formatTime, formatMessage } = useIntl()
  const [openRows, setOpenRows] = useState({})

  const toggleRow = (index, hasDetails) => {
    if (hasDetails) {
      setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }))
    }
  }

  const getParsedValue = (type, value, Component, context, row) => {
    switch (type) {
      case 'date':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value))}
          </Typography>
        )
      case 'datetime':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))}
          </Typography>
        )
      case 'component':
        return <Component value={value} context={context} row={row} />
      case 'responsable':
        const finalValue = row[value] ? `${row[value]} (${row['rol_usuario']})` : null
        return finalValue
      case 'comboDocumentData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `pages.supervision.edit.documents.${value}` })}
          </Typography>
        )
      case 'comboData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `global.basic_table.${value}` })}
          </Typography>
        )
      default:
        return value
    }
  }

  return (
    <TableContainer>
      <Table className={classes.table} aria-label="collapsible table" size={size}>
        {showHeaders && (
          <TableHead>
            <TableRow>
              <TableCell />
              {config.map(({ align = 'left', label }, i) => (
                <TableCell key={i} align={align}>
                  <Typography variant="subtitle1">{label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {data.map((row, i) => {
            const hasDetails = row.fields && row.fields.length > 0

            return (
              <React.Fragment key={rowKey(i, row)}>
                <TableRow
                  className={
                    props.onClick
                      ? props.loading
                        ? classes.loadingRow
                        : classes.clickableRow
                      : classes.row
                  }
                  hover={!!props.onClick}
                  onClick={() => (props.onClick ? props.onClick(row) : null)}
                >
                  <TableCell>
                    {hasDetails && (
                      <IconButton size="small" onClick={() => toggleRow(i, hasDetails)}>
                        {openRows[i] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  </TableCell>
                  {config.map(({ key, align = 'left', type, component }, j) => (
                    <TableCell key={j} align={align}>
                      {getParsedValue(type, row[key], component, context, row)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={config.length + 1}
                  >
                    <Collapse in={openRows[i]} timeout="auto" unmountOnExit>
                      <Box margin={1}>
                        {renderDetails && hasDetails ? renderDetails(row) : null}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
